import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './BookForClient.module.css';
import SquareCardForm from './SquareCardForm';
import Overlay from './Overlay';
import useNotificationChannels from '../useNotificationChannels';

function fmtMoney(cents) {
  return `$${(Number(cents || 0) / 100).toFixed(2)}`;
}
function fmtDateLong(iso) {
  return new Date(iso).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}
function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export default function BookForClient({ API, authHeader, showToast, client, onClose, onBooked }) {
  const [data, setData] = useState(null);
  const [dataError, setDataError] = useState(null);

  const [step, setStep] = useState('service'); // service → slot → review → confirm
  const [variationKey, setVariationKey] = useState(null);
  const [teamMemberId, setTeamMemberId] = useState(null);
  const [date, setDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [slots, setSlots] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [paymentMethod, setPaymentMethod] = useState('none'); // none | balance | card
  const [note, setNote] = useState('');
  const [notify, setNotify] = useState(true);
  const [selectedAthleteIds, setSelectedAthleteIds] = useState([]);

  const channels = useNotificationChannels('bookingConfirmation', API, authHeader);
  const enabledLabel = [channels.email && 'email', channels.sms && 'SMS'].filter(Boolean).join(' + ');
  const anyChannelEnabled = channels.email || channels.sms;
  const cardRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [result, setResult] = useState(null);

  // Load bundle
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = `${API}/admin/square/booking-data${client?.id ? `?clientId=${encodeURIComponent(client.id)}` : ''}`;
        const res = await fetch(url, { headers: { ...authHeader() } });
        const json = await res.json();
        if (cancelled) return;
        if (!res.ok) throw new Error(json.error || 'Failed to load booking data');
        setData(json);
        if (json.staff?.length > 0) setTeamMemberId(json.staff[0].teamMemberId);
        // Default: preselect all of the client's athletes; admin can untoggle
        if (Array.isArray(json.athletes) && json.athletes.length > 0) {
          setSelectedAthleteIds(json.athletes.map(a => a.id));
        }
      } catch (e) {
        if (!cancelled) setDataError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [API, client?.id]);

  // Lock body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const flatVariations = useMemo(() => {
    if (!data) return [];
    const list = [];
    (data.services || []).forEach(s => {
      (s.variations || []).forEach(v => {
        list.push({
          key: v.id,
          serviceName: s.name,
          description: s.description,
          variationName: v.name,
          version: v.version,
          priceCents: v.priceCents,
          durationMs: v.serviceDurationMs,
        });
      });
    });
    return list;
  }, [data]);

  const selected = flatVariations.find(v => v.key === variationKey);
  const perAthleteCents = selected?.priceCents || 0;
  const athleteCount = Math.max(1, selectedAthleteIds.length);
  const priceCents = perAthleteCents * athleteCount;
  const balance = data?.client?.balance ?? 0;
  const selectedAthletes = (data?.athletes || []).filter(a => selectedAthleteIds.includes(a.id));

  // Fetch availability whenever we land on the slot step or selectors change
  useEffect(() => {
    if (step !== 'slot' || !selected || !data?.location?.id) return;
    let cancelled = false;
    (async () => {
      setSlotsLoading(true);
      setSlotsError(null);
      setSlots(null);
      setSelectedSlot(null);
      try {
        const startAt = new Date(`${date}T00:00:00`).toISOString();
        const endDate = new Date(`${date}T00:00:00`);
        endDate.setDate(endDate.getDate() + 1);
        endDate.setHours(endDate.getHours() + 1);
        const endAt = endDate.toISOString();

        const params = new URLSearchParams({
          serviceVariationId: selected.key,
          locationId: data.location.id,
          startAt, endAt,
        });
        if (teamMemberId) params.set('teamMemberId', teamMemberId);

        const res = await fetch(`${API}/square/availability?${params.toString()}`, { headers: { ...authHeader() } });
        const json = await res.json();
        if (cancelled) return;
        if (!res.ok) throw new Error(json.error || 'Availability search failed');
        // Filter slots to the local NGS date (America/Denver). UTC date
        // comparison drops slots after ~6pm MT (they roll to next UTC day).
        const localDateFmt = new Intl.DateTimeFormat('en-CA', {
          timeZone: 'America/Denver', year: 'numeric', month: '2-digit', day: '2-digit',
        });
        const onDate = (json.availabilities || []).filter(a => {
          return localDateFmt.format(new Date(a.startAt)) === date;
        });
        setSlots(onDate);
      } catch (e) {
        if (!cancelled) setSlotsError(e.message);
      } finally {
        if (!cancelled) setSlotsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [step, variationKey, teamMemberId, date, data?.location?.id, API]);

  const submit = async () => {
    const missing = [];
    if (!selected) missing.push('service');
    if (!selectedSlot) missing.push('time slot');
    if (!teamMemberId) missing.push('trainer');
    if (!data?.location?.id) missing.push('location');
    if (missing.length) {
      setSubmitError(`Missing: ${missing.join(', ')}.`);
      return;
    }
    setSubmitError(null);
    setSubmitting(true);
    try {
      const payment = { method: paymentMethod };
      if (paymentMethod !== 'none') payment.amountCents = priceCents;
      if (paymentMethod === 'card') payment.sourceId = await cardRef.current.tokenize();

      const durationMinutes = selected.durationMs ? Math.round(selected.durationMs / 60000) : 30;
      const body = {
        serviceVariationId: selected.key,
        serviceVariationVersion: selected.version,
        teamMemberId,
        locationId: data.location.id,
        startAt: selectedSlot.startAt,
        durationMinutes,
        clientId: client?.id,
        serviceName: selected.serviceName,
        customerNote: note,
        payment,
        notifyParent: notify,
        athleteIds: selectedAthleteIds,
      };

      const res = await fetch(`${API}/square/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Booking failed');
      setResult(json);
      setStep('confirm');
      onBooked?.(json);
      showToast?.('Session booked', 'success');
    } catch (e) {
      setSubmitError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const stepIndex = ['service', 'slot', 'review'].indexOf(step);
  const steps = [
    { key: 'service', label: 'Choose Session' },
    { key: 'slot', label: 'Pick Time' },
    { key: 'review', label: 'Confirm' },
  ];

  return (
    <Overlay className={styles.overlay} onClose={onClose}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div>
            <div className={styles.modalEyebrow}>Book a Session</div>
            <div className={styles.modalTitle}>{client?.name || 'New Booking'}</div>
          </div>
          <button className={styles.modalClose} onClick={onClose} aria-label="Close">×</button>
        </div>

        {dataError && <div className={styles.modalBody}><div className={styles.errorInline}>Couldn't load booking data: {dataError}</div></div>}

        {!data && !dataError && <div className={styles.modalBody}><div className={styles.loading}>Loading booking options…</div></div>}

        {data && flatVariations.length === 0 && step !== 'confirm' && (
          <div className={styles.modalBody}><div className={styles.errorInline}>No bookable services found in Square.</div></div>
        )}

        {data && flatVariations.length > 0 && step !== 'confirm' && (
          <div className={styles.modalBody}>
            <div className={styles.stepper}>
              {steps.map((s, i) => (
                <div key={s.key} className={`${styles.stepItem} ${i <= stepIndex ? styles.stepActive : ''}`}>
                  <div className={styles.stepDot}>{i + 1}</div>
                  <div className={styles.stepLabel}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Step 1: Service */}
            {step === 'service' && (
              <>
                <h3 className={styles.stepTitle}>Choose a Session</h3>
                <div className={styles.serviceGrid}>
                  {flatVariations.map(v => {
                    const isSelected = v.key === variationKey;
                    return (
                      <button key={v.key}
                        className={`${styles.serviceCard} ${isSelected ? styles.serviceCardActive : ''}`}
                        onClick={() => setVariationKey(v.key)}>
                        <div className={styles.serviceName}>{v.serviceName}</div>
                        {v.variationName && v.variationName !== 'Regular' && (
                          <div className={styles.serviceVariation}>{v.variationName}</div>
                        )}
                        {v.description && <div className={styles.serviceDesc}>{v.description}</div>}
                        <div className={styles.serviceMeta}>
                          {v.durationMs ? `${Math.round(v.durationMs / 60000)} min` : ''}
                          {v.priceCents != null ? <span className={styles.servicePrice}>{fmtMoney(v.priceCents)}</span> : null}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {data.athletes?.length > 0 && (
                  <div className={styles.fieldBlock}>
                    <label className={styles.fieldLabel}>
                      Who is training?
                      {data.athletes.length > 1 && <span style={{ color: 'var(--gray-3)', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}> · tap to toggle (each athlete is billed at the session rate)</span>}
                    </label>
                    <div className={styles.athleteChips}>
                      {data.athletes.map(a => {
                        const on = selectedAthleteIds.includes(a.id);
                        return (
                          <button key={a.id} type="button"
                            className={`${styles.athleteChip} ${on ? styles.athleteChipOn : ''}`}
                            onClick={() => setSelectedAthleteIds(prev =>
                              on ? prev.filter(x => x !== a.id) : [...prev, a.id]
                            )}>
                            {on ? '✓ ' : ''}{a.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {data.staff?.length > 1 && (
                  <div className={styles.fieldBlock}>
                    <label className={styles.fieldLabel}>Trainer</label>
                    <select className={styles.select} value={teamMemberId || ''} onChange={e => setTeamMemberId(e.target.value || null)}>
                      {data.staff.map(s => <option key={s.teamMemberId} value={s.teamMemberId}>{s.displayName}</option>)}
                    </select>
                  </div>
                )}

                <div className={styles.actions}>
                  <button className={styles.secondaryBtn} onClick={onClose}>Cancel</button>
                  <button
                    className={styles.primaryBtn}
                    disabled={!variationKey || (data.athletes?.length > 0 && selectedAthleteIds.length === 0)}
                    onClick={() => setStep('slot')}
                  >
                    Next: Pick a Time →
                  </button>
                </div>
              </>
            )}

            {/* Step 2: Slot */}
            {step === 'slot' && selected && (
              <>
                <h3 className={styles.stepTitle}>Pick a Time</h3>
                <div className={styles.sessionSummary}>
                  {selected.serviceName} · {selected.durationMs ? `${Math.round(selected.durationMs / 60000)} min` : ''} · {fmtMoney(priceCents)}
                </div>

                <div className={styles.fieldBlock}>
                  <label className={styles.fieldLabel}>Date</label>
                  <input type="date" className={styles.select} value={date} onChange={e => setDate(e.target.value)}
                    min={new Date().toISOString().slice(0, 10)} />
                </div>

                {slotsLoading && <div className={styles.loading}>Loading available times…</div>}
                {slotsError && <div className={styles.errorInline}>{slotsError}</div>}
                {slots && slots.length === 0 && !slotsLoading && (
                  <div className={styles.emptyNotice}>No open times on this date. Try another date.</div>
                )}
                {slots && slots.length > 0 && (
                  <div className={styles.slotGrid}>
                    {slots.map((s, i) => (
                      <button key={i}
                        className={`${styles.slot} ${selectedSlot?.startAt === s.startAt ? styles.slotActive : ''}`}
                        onClick={() => setSelectedSlot(s)}>
                        {fmtTime(s.startAt)}
                      </button>
                    ))}
                  </div>
                )}

                <div className={styles.actions}>
                  <button className={styles.secondaryBtn} onClick={() => setStep('service')}>← Back</button>
                  <button className={styles.primaryBtn} disabled={!selectedSlot} onClick={() => setStep('review')}>
                    Next: Confirm →
                  </button>
                </div>
              </>
            )}

            {/* Step 3: Review + payment */}
            {step === 'review' && selected && selectedSlot && (
              <>
                <h3 className={styles.stepTitle}>Confirm &amp; Pay</h3>

                <div className={styles.reviewBox}>
                  <div className={styles.reviewRow}><span>Client</span><strong>{client?.name || '—'}</strong></div>
                  <div className={styles.reviewRow}><span>Session</span><strong>{selected.serviceName}</strong></div>
                  <div className={styles.reviewRow}><span>Date</span><strong>{fmtDateLong(selectedSlot.startAt)}</strong></div>
                  <div className={styles.reviewRow}><span>Time</span><strong>{fmtTime(selectedSlot.startAt)}</strong></div>
                  <div className={styles.reviewRow}><span>Duration</span><strong>{selected.durationMs ? `${Math.round(selected.durationMs / 60000)} min` : '—'}</strong></div>
                  {selectedAthletes.length > 0 && perAthleteCents > 0 ? (
                    <>
                      {selectedAthletes.map(a => (
                        <div key={a.id} className={styles.reviewRow}>
                          <span>{a.name}</span><strong>{fmtMoney(perAthleteCents)}</strong>
                        </div>
                      ))}
                      <div className={`${styles.reviewRow} ${styles.reviewTotal}`}>
                        <span>Total</span><strong>{fmtMoney(priceCents)}</strong>
                      </div>
                    </>
                  ) : (
                    <div className={`${styles.reviewRow} ${styles.reviewTotal}`}><span>Price</span><strong>{fmtMoney(priceCents)}</strong></div>
                  )}
                </div>

                <div className={styles.fieldBlock}>
                  <label className={styles.fieldLabel}>Note for Michael (optional)</label>
                  <textarea className={styles.textarea} rows={2} value={note} onChange={e => setNote(e.target.value)}
                    placeholder="Internal note attached to the booking…" />
                </div>

                <label className={styles.fieldLabel} style={{ marginTop: 8 }}>Payment</label>
                <div className={styles.payOptions}>
                  <button
                    className={`${styles.payOption} ${paymentMethod === 'none' ? styles.payOptionActive : ''}`}
                    onClick={() => setPaymentMethod('none')}>
                    <div className={styles.payOptionTitle}>Schedule Only</div>
                    <div className={styles.payOptionSub}>No charge — bill later</div>
                  </button>
                  <button
                    className={`${styles.payOption} ${paymentMethod === 'balance' ? styles.payOptionActive : ''}`}
                    onClick={() => setPaymentMethod('balance')}
                    disabled={!client?.id || priceCents <= 0}>
                    <div className={styles.payOptionTitle}>Account Balance</div>
                    <div className={styles.payOptionSub}>
                      Current: ${Number(balance).toFixed(2)}
                      {priceCents > 0 && balance * 100 < priceCents && (
                        <span className={styles.payOptionWarn}> · will go negative</span>
                      )}
                    </div>
                  </button>
                  <button
                    className={`${styles.payOption} ${paymentMethod === 'card' ? styles.payOptionActive : ''}`}
                    onClick={() => setPaymentMethod('card')}
                    disabled={priceCents <= 0}>
                    <div className={styles.payOptionTitle}>Pay by Card</div>
                    <div className={styles.payOptionSub}>Charge via Square</div>
                  </button>
                </div>

                {paymentMethod === 'card' && data.payments && (
                  <div className={styles.fieldBlock}>
                    <label className={styles.fieldLabel}>Card Details</label>
                    <SquareCardForm ref={cardRef} config={data.payments} />
                  </div>
                )}

                {(client?.email || client?.phone) && (
                  <label className={`${styles.checkboxRow} ${!anyChannelEnabled ? styles.checkboxDisabled : ''}`}>
                    <input
                      type="checkbox"
                      checked={notify && anyChannelEnabled}
                      disabled={!anyChannelEnabled}
                      onChange={e => setNotify(e.target.checked)}
                    />
                    <span>
                      Notify {client?.name?.split(' ')[0] || 'parent'}{' '}
                      <span style={{ color: 'var(--gray-3)' }}>
                        ({anyChannelEnabled ? enabledLabel : 'no channels enabled — turn on in Notifications'})
                      </span>
                    </span>
                  </label>
                )}

                {submitError && <div className={styles.errorInline}>{submitError}</div>}

                <div className={styles.actions}>
                  <button className={styles.secondaryBtn} onClick={() => setStep('slot')} disabled={submitting}>← Back</button>
                  <button className={styles.primaryBtn} disabled={submitting} onClick={submit}>
                    {submitting ? 'Booking…' :
                      paymentMethod === 'card' ? `Book & Charge ${fmtMoney(priceCents)}` :
                      paymentMethod === 'balance' ? `Book & Deduct ${fmtMoney(priceCents)}` :
                      'Book Session'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {step === 'confirm' && result && (
          <div className={styles.modalBody}>
            <div className={styles.confirmCheck}>✓</div>
            <div className={styles.confirmTitle}>Session Booked</div>
            <div className={styles.confirmSub}>
              {result.emailed
                ? `Confirmation sent to ${client?.email}.`
                : 'No email was sent.'}
            </div>
            <div className={styles.confirmDetails}>
              {selectedAthletes.length > 0 && (
                <div className={styles.detailRow}>
                  <span>{selectedAthletes.length > 1 ? 'Athletes' : 'Athlete'}</span>
                  <strong>{selectedAthletes.map(a => a.name).join(' & ')}</strong>
                </div>
              )}
              <div className={styles.detailRow}><span>Session</span><strong>{selected?.serviceName}</strong></div>
              <div className={styles.detailRow}><span>Date</span><strong>{fmtDateLong(result.booking.startAt)}</strong></div>
              <div className={styles.detailRow}><span>Time</span><strong>{fmtTime(result.booking.startAt)}</strong></div>
              {paymentMethod === 'card' && (
                <div className={styles.detailRow}><span>Total paid by card</span><strong>{fmtMoney(priceCents)}</strong></div>
              )}
              {paymentMethod === 'balance' && (
                <>
                  <div className={styles.detailRow}><span>Total deducted from balance</span><strong>{fmtMoney(priceCents)}</strong></div>
                  <div className={styles.detailRow}><span>Remaining balance</span><strong>${Number(result.newBalance).toFixed(2)}</strong></div>
                </>
              )}
              {paymentMethod === 'none' && (
                <div className={styles.detailRow}><span>Charge</span><strong>None — bill later</strong></div>
              )}
            </div>
            <div className={styles.actions}>
              <button className={styles.secondaryBtn} onClick={() => {
                setResult(null); setStep('service'); setVariationKey(null);
                setSelectedSlot(null); setPaymentMethod('none'); setNote('');
                setSelectedAthleteIds(data.athletes?.map(a => a.id) || []);
              }}>Book Another</button>
              <button className={styles.primaryBtn} onClick={onClose}>Done</button>
            </div>
          </div>
        )}
      </div>
    </Overlay>
  );
}
